// The right pane of the Inbox: a notice for me, a notice I sent, a conversation.
import { useEffect, useRef, useState } from 'react'
import { offline } from '../lib/offline'
import { openKeys } from '../lib/approvalsCore'
import * as D from './data'
import { ackLine, ackStats, LINK_LABEL, LINK_PAGES, linkKind, noticePdfHtml, noticeProgress, threadTitle, validMessage, when } from './core'
import ContactButtons from './ContactButtons'
import Translate from './Translate'
import { Back, Plain, Tag, useIsOffline } from './InboxBits'
import { ReplyBox } from './InboxCompose'

export const remindText = r => 'Reminded ' + r.reminded + ' in the app.'
const dateLine = (...a) => a.filter(Boolean).join(' · ')
const due = n => (n.due_date ? 'due ' + String(n.due_date).slice(0, 10) : '')

function Attachment({ notice }) {
  const [err, setErr] = useState('')
  if (!notice.attachment_path) return null
  const openIt = async () => { setErr(''); const w = window.open('', '_blank'); try { const u = await D.fileUrl(notice.attachment_path); if (w) { w.opener = null; w.location.href = u } else window.location.href = u } catch (e) { if (w) w.close(); setErr(e.message) } }
  return <div style={{ marginTop: 12 }}><button type="button" className="hci-btn" onClick={openIt}>📎 {notice.attachment_name || 'Attachment'}</button>{err && <div className="hci-err">{err}</div>}</div>
}
function LinkButton({ notice, config, setSelectedVessel }) {
  if (!notice.link_type || !notice.link_id) return null
  const go = () => {
    const k = linkKind(notice.link_type), target = k ? openKeys(k, notice.link_id, notice.link_label) : { page: LINK_PAGES[notice.link_type] }
    try { if (target.key) localStorage.setItem(target.key, target.value); Object.entries(target.extra || {}).forEach(([a, b]) => localStorage.setItem(a, b)) } catch (e) {}
    if (config && config.accountType === 'company' && notice.link_client && setSelectedVessel) { const v = (config.fleet || []).find(x => x.id === notice.link_client); if (v) setSelectedVessel(v) }
    window.dispatchEvent(new CustomEvent('hc:navigate', { detail: { page: target.page } }))
  }
  return <div style={{ marginTop: 12 }}><button type="button" className="hci-btn" onClick={go}>{LINK_LABEL[notice.link_type]}: {notice.link_label || 'Open'}</button></div>
}

// a notice for me: read it, then the one big button
export function NoticeDetail({ row, uid, config, setSelectedVessel, onBack, onPatch }) {
  const n = row.comms_notices || {}, [r, setR] = useState(row), [err, setErr] = useState(''), [busy, setBusy] = useState(false), [todo, setTodo] = useState(''), off = useIsOffline()
  useEffect(() => { if (!row.read_at) { const at = new Date().toISOString(); D.markRead(row.id).then(() => onPatch(row.id, { read_at: at })) } }, [row.id]) // eslint-disable-line
  const ack = async () => {
    setBusy(true); setErr('')
    try { await D.ack(r.id); const at = new Date().toISOString(), patch = { acked: true, acked_at: r.acked_at || at, read_at: r.read_at || at, waiting: offline.isOffline() }; setR(x => ({ ...x, ...patch })); onPatch(r.id, patch) } catch (e) { setErr(e.message) } finally { setBusy(false) }
  }
  const toTodo = async () => { setTodo('…'); try { await D.noticeToTodo(n, r.client_id, r.person_name); setTodo('Added to the To Do list "Inbox".') } catch (e) { setTodo(off ? 'To Do from a notice needs a connection.' : 'Could not add it: ' + e.message) } }
  return (
    <>
      <div className="hci-open-head">
        <div className="hci-open-top"><Back onClick={onBack} /><h2>{n.title}</h2>{n.priority === 'important' && <Tag tone="red">Important</Tag>}</div>
        <div className="hci-sub">{dateLine(n.sender_name, when(n.sent_at), due(n))}</div>
      </div>
      <div className="hci-scroll">
        <Plain text={n.body} />
        <div style={{ marginTop: 8 }}><Translate kind="notice" id={n.id} /></div>
        <Attachment notice={n} /><LinkButton notice={n} config={config} setSelectedVessel={setSelectedVessel} />
        <div className="hci-acts"><button type="button" className="hci-btn" onClick={toTodo}>Add to my To Do</button>{todo && <span className="hci-sub">{todo}</span>}</div>
      </div>
      {(n.ack_required || err) && <div className="hci-open-foot">
        {n.ack_required && (r.acked
          ? <div><Tag tone="green">{r.waiting ? 'Acknowledged: goes when back online' : 'Acknowledged ' + when(r.acked_at)}</Tag></div>
          : <button type="button" className="hci-primary hci-big" disabled={busy} onClick={ack} data-comms-ack>Read &amp; acknowledge</button>)}
        {err && <div className="hci-err">{err}</div>}
      </div>}
    </>
  )
}

const Recipient = ({ r }) => (
  <div data-recipient={r.person_name} className="hci-rec">
    <div style={{ flex: 1, minWidth: 150 }}><b style={{ fontWeight: 600, fontSize: 13.5 }}>{r.person_name}</b><div className="hci-sub">{dateLine(r.person_role, r.vessel_name)}</div></div>
    <div style={{ fontSize: 12.5, color: r.acked ? 'var(--hci-green)' : 'var(--muted)' }}>{ackLine(r)}{r.remind_count ? ' · reminded ' + r.remind_count + 'x' : ''}</div>
  </div>
)

// a notice I sent: the live list, remind, the PDF record
export function SentDetail({ notice: n, onBack, onChanged }) {
  const [rows, setRows] = useState(null), [note, setNote] = useState(''), [busy, setBusy] = useState(false)
  const load = () => D.recipients(n.id).then(setRows).catch(e => setNote(e.message))
  useEffect(() => { load(); const t = setInterval(load, 20000); window.addEventListener('focus', load); return () => { clearInterval(t); window.removeEventListener('focus', load) } }, [n.id]) // eslint-disable-line
  const s = ackStats(rows), p = noticeProgress(n, rows)
  const remind = async () => { setBusy(true); setNote(''); try { setNote(remindText(await D.remind(n.id))); load(); onChanged() } catch (e) { setNote(e.message) } finally { setBusy(false) } }
  const pdf = async () => {
    setBusy(true); setNote('')
    try {
      const { default: html2pdf } = await import('html2pdf.js')
      const el = document.createElement('div'); el.innerHTML = noticePdfHtml(n, rows, new Date().toISOString())   // every value is escaped in noticePdfHtml
      await html2pdf().set({ margin: 10, filename: ('Notice_' + n.title).replace(/[^a-z0-9]+/gi, '_').slice(0, 80) + '.pdf', pagebreak: { mode: ['css', 'legacy'] }, html2canvas: { scale: 2 }, jsPDF: { unit: 'mm', format: 'a4' } }).from(el.firstElementChild).save()
    } catch (e) { setNote('The PDF could not be made: ' + e.message) } finally { setBusy(false) }
  }
  return (
    <>
      <div className="hci-open-head">
        <div className="hci-open-top"><Back onClick={onBack} /><h2>{n.title}</h2>{n.priority === 'important' && <Tag tone="red">Important</Tag>}</div>
        <div className="hci-sub">{dateLine(n.audience, when(n.sent_at), due(n))}</div>
      </div>
      <div className="hci-scroll">
        <Plain text={n.body} />
        <div style={{ marginTop: 6 }}><Translate kind="notice" id={n.id} /></div>
        <Attachment notice={n} />
        <div className="hci-progress-card">
          <b data-comms-count style={{ fontSize: 14 }}>{!rows ? 'Loading…' : p.label}</b>
          {rows && <div className="hci-bar" role="progressbar" aria-valuemin={0} aria-valuemax={p.total} aria-valuenow={p.done} aria-label={p.label}><i className={p.all ? 'all' : ''} style={{ width: p.pct + '%' }} /></div>}
          <div className="hci-acts">
            {n.ack_required && s.missing > 0 && <button type="button" className="hci-primary" disabled={busy} onClick={remind}>Remind those missing ({s.missing})</button>}
            <button type="button" className="hci-btn" disabled={busy || !rows} onClick={pdf}>Export PDF</button>
          </div>
          {note && <div className="hci-note">{note}</div>}
        </div>
        {rows && n.ack_required && <>
          <div className="hci-rec-h">Not yet ({s.missing})</div>{s.not_yet.map(r => <Recipient key={r.id} r={r} />)}
          <div className="hci-rec-h">Acknowledged ({s.acked})</div>{s.acknowledged.map(r => <Recipient key={r.id} r={r} />)}
        </>}
        {rows && !n.ack_required && <div style={{ marginTop: 14 }}>{rows.map(r => <Recipient key={r.id} r={r} />)}</div>}
      </div>
    </>
  )
}

function Photo({ path }) {
  const [src, setSrc] = useState(null)
  useEffect(() => { let live = true; D.fileUrl(path).then(u => { if (live) setSrc(u) }).catch(() => {}); return () => { live = false } }, [path])
  return src ? <img src={src} alt="Attached" style={{ display: 'block', maxWidth: '100%', maxHeight: 280, borderRadius: 8, marginTop: 4 }} /> : <div className="hci-sub">📷 Photo</div>
}

// one person in a conversation: Email / WhatsApp / Call only when the database gave this login the detail (crew_personal_perm, sql/comms.sql)
function PersonLine({ m, people, one }) {
  const p = (people || []).find(x => x.user_id === m.user_id)
  return <div data-thread-member className="hci-person"><span>{one ? m.person_role : <><b>{m.person_name}</b>{[m.person_role, p && p.vessel_name].filter(Boolean).map(x => ' · ' + x).join('')}</>}</span>{p && <ContactButtons email={p.email} phone={p.phone} subject="Hey Captain" />}</div>
}

export function Thread({ thread, uid, people, onBack, onRead, onSent }) {
  const [rows, setRows] = useState(null), [err, setErr] = useState(''), [busy, setBusy] = useState(false), [group, setGroup] = useState(false), end = useRef(null)
  const member = (thread.comms_thread_members || []).find(m => m.user_id === uid), others = (thread.comms_thread_members || []).filter(m => m.user_id !== uid)
  const load = () => D.messages(thread.id).then(setRows).catch(e => { setRows(r => r || []); setErr(e.message) })
  useEffect(() => { load(); if (member) D.markThreadRead(member.id).then(() => onRead(thread)); const t = setInterval(load, 20000); return () => clearInterval(t) }, [thread.id]) // eslint-disable-line
  useEffect(() => { if (end.current && end.current.scrollIntoView) end.current.scrollIntoView({ block: 'nearest' }) }, [rows && rows.length])
  const send = async (text, photo) => {
    setErr('')
    const bad = validMessage(text, !!photo)
    if (bad) { setErr(bad); return false }
    setBusy(true)
    try { const up = photo ? await D.upload(photo) : null; await D.sendMessage({ threadId: thread.id, uid, body: text.trim(), photo: up }); await load(); onSent(); return true }
    catch (e) { setErr(e.message); return false } finally { setBusy(false) }
  }
  return (
    <>
      <div className="hci-open-head">
        <div className="hci-open-top"><Back onClick={onBack} /><h2 data-thread-title>{threadTitle(thread, uid, people)}</h2></div>
        {others.length === 1 ? <PersonLine m={others[0]} people={people} one /> : others.length > 1 && <>
          <div><button type="button" className="hci-link" aria-expanded={group} onClick={() => setGroup(g => !g)}>{others.length} people {group ? '▴' : '▾'}</button></div>
          {group && others.map(m => <PersonLine key={m.id} m={m} people={people} />)}
        </>}
      </div>
      <div className="hci-scroll">
        <div className="hci-msgs">
          {(rows || []).map(m => { const mine = m.sender_id === uid; return (
            <div key={m.id} data-message={m.id} className={'hci-msg' + (mine ? ' mine' : '')}>
              <small>{mine ? 'You' : m.sender_name}{m.created_at ? ' · ' + when(m.offline_at || m.created_at) : ' · waiting to sync'}</small>
              {m.body && <Plain text={m.body} className="hci-text" />}
              {m.photo_path && <Photo path={m.photo_path} />}
              {m.created_at && m.body && !mine && <Translate kind="message" id={m.id} />}
            </div>
          ) })}
          <div ref={end} />
        </div>
      </div>
      <div className="hci-open-foot"><ReplyBox onSend={send} busy={busy} err={err} /></div>
    </>
  )
}
